import { useUser } from '@hooks/user'

import { NextSeo } from 'next-seo'
import { Header } from '@components/Header'
import { Footer } from '@components/Footer'
import { SignupForm } from '@components/checkout/CheckoutForm/UserAccount/SignupForm'
import { Formik, Form, FormikErrors } from 'formik'
import { SignupDetails } from '@models/user'
import { Button } from '@components/UI/Button'
import { useRouter } from 'next/router'
import toast from 'react-hot-toast'
import { Spinner } from '@components/UI/Spinner'
import { LoginDialog } from '@components/LoginDialog'
import { useState } from 'react'

export interface FormValues {
  signup: SignupDetails
}

export const initialFormValues: FormValues = {
  signup: {
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    acceptTOC: false,
    type: ''
  }
}

export default function Signup() {
  const { user, register } = useUser()
  const { push, query } = useRouter()
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  async function handleSubmit(values: FormValues) {
    try {
      const viewToken = query.newUserWithViewPrivToken as string
      if (viewToken) {
        await register.mutateAsync({
          firstName: values.signup.firstName,
          lastName: values.signup.lastName,
          email: values.signup.email,
          phoneNumber: values.signup.phoneNumber,
          password: values.signup.password,
          type: values.signup.type,
          viewToken: `${query.newUserWithViewPrivToken}`
        })

        const reportId = JSON.parse(atob(viewToken.split('.')[1]))
          .domoreport_log_id

        void push(`/domoreport/${parseInt(reportId)}`)
      } else {
        await register.mutateAsync({
          firstName: values.signup.firstName,
          lastName: values.signup.lastName,
          email: values.signup.email,
          phoneNumber: values.signup.phoneNumber,
          password: values.signup.password,
          type: values.signup.type
        })

        void push('/')
      }
    } catch (error) {
      if (error.response.status === 409) {
        toast.error('A user with that information already exists')
      }
    }
  }

  function validate(values: FormValues) {
    const errors: FormikErrors<FormValues> = {}
    if (!user.data) {
      if (values.signup.password !== values.signup.confirmPassword) {
        errors.signup ??= {}
        errors.signup.confirmPassword =
          'Your password confirmation must match your password'
      }
      if (!values.signup.type) {
        errors.signup ??= {}
        errors.signup.type = 'Please select the most relevant profession'
      }
      if (!values.signup.acceptTOC) {
        errors.signup ??= {}
        errors.signup.acceptTOC = 'Please accept the terms and conditions'
      }
    }
    return errors
  }

  function open() {
    setIsDialogOpen(true)
  }
  function close() {
    setIsDialogOpen(false)
  }

  return (
    <>
      <NextSeo
        title="Sign Up"
        description="New to Majordomo? Local and accurate Home inspection repair estimates in 24-hours or less. 24-hour Instant chat, log-in Create an Account, Forgot Password"
      />
      <LoginDialog
        isOpen={isDialogOpen}
        redirectUrl="/my-properties/domoreport"
        handleClose={close}
      />

      <Header />

      <main className="pt-8 px-0 container mx-auto">
        <div className="flex md:flex-row flex-col justify-between items-center lg:pb-36 pb-12">
          <img
            src="/images/product-pages/signup-hero.png"
            className="md:inline hidden order-first mb-0 w-1/2"
          />

          <div className="flex flex-col md:items-start items-center justify-center md:w-1/2 w-full 2xl:p-20 xl:p-12 md:p-8 md:order-last order-first">
            <h1 className="font-black tracking-tight mb-8 text-3xl text-center w-full">
              Sign Up
            </h1>
            <Formik
              initialValues={initialFormValues}
              validate={validate}
              onSubmit={handleSubmit}
            >
              <Form className="w-full px-8">
                <SignupForm openLoginModal={open} />
                <section className="flex justify-center mt-8">
                  {register.status === 'loading' ? (
                    <Spinner />
                  ) : (
                    <Button
                      variant="secondary"
                      clipSide="right"
                      data-testid="signupSubmit"
                    >
                      Sign Up
                    </Button>
                  )}
                </section>
              </Form>
            </Formik>
          </div>
        </div>
      </main>

      <Footer />
    </>
  )
}
